/**
 * DASH viewer widgets
 *
 * Read-only renderers for property values, one per dash:viewer. They show
 * values only; nothing here is editable and no form controls are used.
 * See https://datashapes.org/forms.html#viewers for the DASH definitions.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DVIEW, pickLang, setting } from '@utils/viewShapes.utils';
import { VALUE_LIMIT } from '@services/entityView.service';
import { isUriValued } from '@utils/literalFormat.utils';
import { pickWkt } from '@hooks/useGeometry';
import type { EntityObject } from '@/types';
import EntityMap from '../../EntityMap';
import { documentName, SymbolViewer } from './figures';
import { AnyValue, recordName, UriLink, uriText, useLiteralText } from './values';
import { Chip } from './chip';
import type { Widget, WidgetContext, WidgetProps } from './types';
import styles from '../EntityShapeView.module.scss';
import figureStyles from './widgets.module.scss';

const DATA_NS = 'https://data.banenor.no/data/';

const NORWEGIAN = ['nb', 'no', 'nn'];
const VALUE_TABLE_PREVIEW = 8;

function ValueList({ children }: { children: JSX.Element[] }): JSX.Element {
  if (children.length === 1) return children[0];
  return (
    <ul className={styles.valueList}>
      {children.map((child, index) => (
        <li key={index}>{child}</li>
      ))}
    </ul>
  );
}

/** dash:LabelViewer: display text without a link. */
export const LabelViewer: Widget = ({ values, context }) => {
  const literalText = useLiteralText();
  return (
    <ValueList>
      {values.map((value) => (
        <span key={value.value}>
          {isUriValued(value) ? uriText(value.value, context) : literalText(value)}
        </span>
      ))}
    </ValueList>
  );
};

/** dash:LiteralViewer: literals formatted for the UI locale (dates, numbers, booleans). */
export const LiteralViewer: Widget = ({ values, context }) => (
  <ValueList>
    {values.map((value) => (
      <AnyValue key={`${value.value}@${value.lang ?? ''}`} value={value} context={context} />
    ))}
  </ValueList>
);

/** Values in the UI language (Norwegian variants count as one), else untagged, else all. */
export function valuesForLanguage(values: EntityObject[], language: string): EntityObject[] {
  const base = (language || 'en').split('-')[0].toLowerCase();
  const family = NORWEGIAN.includes(base) ? NORWEGIAN : [base];
  const inLanguage = values.filter(
    (value) => value.lang && family.includes(value.lang.toLowerCase())
  );
  if (inLanguage.length > 0) return inLanguage;
  const untagged = values.filter((value) => !value.lang);
  if (untagged.length > 0) return untagged;
  const english = values.filter((value) => value.lang?.toLowerCase() === 'en');
  return english.length > 0 ? english : values;
}

/** dash:LangStringViewer: the value(s) in the UI language, other languages on hover. */
export const LangStringViewer: Widget = ({ values, context }) => {
  const shown = valuesForLanguage(values, context.language);
  const others = values.filter((value) => !shown.includes(value));
  const title =
    others.length > 0
      ? others.map((value) => `${value.value} @${value.lang ?? ''}`).join('\n')
      : undefined;
  return (
    <ValueList>
      {shown.map((value) => (
        <span key={`${value.value}@${value.lang ?? ''}`} title={title}>
          {value.value}
        </span>
      ))}
    </ValueList>
  );
};

/** dash:URIViewer: links to the entity pages of the values. */
export const URIViewer: Widget = ({ property, values, context }) =>
  // In an at a glance card: the records as pills, each name once (the versions of a track are one)
  context.summary && values.every((value) => value.value.startsWith(DATA_NS)) ? (
    <span className={figureStyles.summaryChips}>
      {[
        ...new Map(
          values.map((value) => {
            const classLabel = property.classIri
              ? (context.resolved[property.classIri]?.[0] ?? null)
              : null;
            const name = recordName(
              value.value,
              context,
              classLabel ?? classNameOf(property.classIri, context)
            );
            return [name, { iri: value.value, name }] as const;
          })
        ).values(),
      ].map(({ iri, name }) => (
        <Chip key={iri} iri={iri} icon={setting(property, 'icon')} label={name} context={context} />
      ))}
    </span>
  ) : (
    <ValueList>
      {values.map((value) =>
        // dview:show "document": a web page outside the graph, named by its address (an emergency card)
        setting(property, 'show') === 'document' &&
        /^https?:/.test(value.value) &&
        !value.value.startsWith(DATA_NS) ? (
          <a
            key={value.value}
            href={value.value}
            target="_blank"
            rel="noopener noreferrer"
            title={value.value}
            className={figureStyles.document}
          >
            {documentName(value.value)} ↗
          </a>
        ) : (
          <AnyValue key={value.value} value={value} context={context} />
        )
      )}
    </ValueList>
  );

/** The label of a class from the shapes (a node shape targeting it). */
function classNameOf(classIri: string | null, context: WidgetContext): string | null {
  if (!classIri) return null;
  const shape = context.model?.shapes?.nodeShapes.find((node) =>
    node.targetClasses.includes(classIri)
  );
  return shape ? pickLang(shape.label, context.language) : null;
}

/** dash:ValueTableViewer: many related objects as a counted, expandable list. */
export const ValueTableViewer: Widget = ({ values, context }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const sorted = [...values].sort((a, b) =>
    uriText(a.value, context).localeCompare(uriText(b.value, context), undefined, { numeric: true })
  );
  const visible = expanded ? sorted.slice(0, VALUE_LIMIT) : sorted.slice(0, VALUE_TABLE_PREVIEW);
  const truncated = values.length > VALUE_LIMIT;

  return (
    <div className={styles.valueTable}>
      <span className={styles.valueCount}>
        {truncated ? t('entity.view.moreThan', { count: VALUE_LIMIT }) : values.length}
      </span>
      <ul className={styles.valueTableList}>
        {visible.map((value) => (
          <li key={value.value}>
            <AnyValue value={value} context={context} />
          </li>
        ))}
      </ul>
      {sorted.length > VALUE_TABLE_PREVIEW && (
        <button type="button" className={styles.showMore} onClick={() => setExpanded(!expanded)}>
          {expanded
            ? t('entity.showLess')
            : t('entity.showMore', {
                count: Math.min(sorted.length, VALUE_LIMIT) - VALUE_TABLE_PREVIEW,
              })}
        </button>
      )}
    </div>
  );
};

/** One nested node as "name: value" pairs, following its node shape. */
function NestedNode({ uri, context }: { uri: string; context: WidgetContext }): JSX.Element {
  const shape = context.nestedShape;
  const nodeValues = context.nestedValues[uri];
  if (!shape || !nodeValues) return <UriLink uri={uri} context={context} />;

  const parts = shape.properties
    .filter((property) => !property.hidden && (nodeValues[property.id]?.length ?? 0) > 0)
    .sort((a, b) => a.order - b.order)
    .map((property) => ({ property, values: nodeValues[property.id] }));
  if (parts.length === 0) return <UriLink uri={uri} context={context} />;

  return (
    <span className={styles.details}>
      {parts.map(({ property, values }) =>
        // A part the shapes give a picture (a type designator's TJN paragraph) draws it instead of its name
        property.viewer === DVIEW.SymbolViewer ? (
          <SymbolViewer
            key={property.id}
            property={property}
            values={values}
            context={{ ...context, nestedShape: null }}
          />
        ) : (
          <span key={property.id} className={styles.detailsPart}>
            <span className={styles.detailsName}>{pickLang(property.name, context.language)}</span>
            <LiteralViewer
              property={property}
              values={values}
              context={{ ...context, nestedShape: null }}
            />
          </span>
        )
      )}
    </span>
  );
}

/** dash:DetailsViewer: nested nodes (designators, element states, ...) shown inline. */
export const DetailsViewer: Widget = ({ values, context }) => (
  <ValueList>
    {values.map((value) =>
      value.type === 'uri' ? (
        <NestedNode key={value.value} uri={value.value} context={context} />
      ) : (
        <AnyValue key={value.value} value={value} context={context} />
      )
    )}
  </ValueList>
);

/** WKT geometry on a map (not a DASH viewer; picked for gsp:wktLiteral values). */
export const GeometryViewer: Widget = ({ values }) => {
  const wkt = pickWkt(values);
  return wkt ? <EntityMap wkt={wkt} /> : null;
};

export type { WidgetProps };
